import { useLocation } from 'wouter';
import type { AuthedUser } from '../lib/auth-types';
import { TeamPage } from '../pages/TeamPage';
import { ToolsProfileForm } from './ToolsProfileForm';
import { ToolsTopbar } from './ToolsTopbar';

/**
 * ToolsSettings — schlanke tools-eigene Settings-Shell (P1c PR-B, D-P1c-9/13).
 *
 * KEIN Import der DAM-`SettingsShell` (die ist Layout-/Lazy-gekoppelt). Tabs:
 * **Profil** (alle User, tools-eigene `ToolsProfileForm`) + **Team** (admin-only,
 * die self-contained DAM-`TeamPage` importiert — NICHT kopiert). Vocab bleibt DAM.
 *
 * Team-Gating ist doppelt: die Route in ToolsApp redirected Member weg (Client-UX),
 * das Backend `assertAdmin` bleibt die echte Boundary (403). `user.isAdmin`
 * blendet den Tab + Panel für Member aus (defense-in-depth).
 */
interface ToolsSettingsProps {
  user: AuthedUser;
  onLogout: () => void;
  onProfileSaved: (displayName: string) => void;
  tab: 'profile' | 'team';
}

export function ToolsSettings({ user, onLogout, onProfileSaved, tab }: ToolsSettingsProps) {
  const [, navigate] = useLocation();

  return (
    <div className="min-h-screen bg-brand-cream">
      <ToolsTopbar user={user} onLogout={onLogout} />
      <main className="mx-auto w-full max-w-6xl px-4 md:px-6 py-8">
        {/* Tools-App behält den Display-Titel (eigene Identität, Redesign S2 out of scope). */}
        <h1 className="font-display text-4xl font-bold text-brand-green-700 uppercase">Einstellungen</h1>

        <nav aria-label="Einstellungen" className="mt-6 flex gap-1 border-b border-brand-green-900/10">
          <TabButton active={tab === 'profile'} onClick={() => navigate('/settings/profile')}>
            Profil
          </TabButton>
          {user.isAdmin && (
            <TabButton active={tab === 'team'} onClick={() => navigate('/settings/team')}>
              Team
            </TabButton>
          )}
        </nav>

        <div className="mt-6">
          {tab === 'team' && user.isAdmin ? (
            <TeamPage authedUser={user} />
          ) : (
            <ToolsProfileForm
              user={{ email: user.email, displayName: user.displayName, role: user.role }}
              onSaved={onProfileSaved}
            />
          )}
        </div>
      </main>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 ${
        active
          ? 'border-brand-green-700 text-brand-green-700'
          : 'border-transparent text-brand-green-600 hover:text-brand-green-800'
      }`}
    >
      {children}
    </button>
  );
}
